import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtCurrency } from "../../lib/api";
import MobileHeader from "../../components/MobileHeader";
import { Button } from "../../components/ui/button";
import { Slider } from "../../components/ui/slider";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { CheckCircle2, Truck, Bike, Car, Plus, Navigation, Fuel, Gauge, Clock } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";

const VEHICLE_TYPES = [
    { value: "truck", label: "Truck", icon: Truck },
    { value: "van", label: "Van / Pickup", icon: Truck },
    { value: "bike", label: "Bike / Okada", icon: Bike },
    { value: "tricycle", label: "Tricycle / Keke", icon: Car },
];

const PURPOSES = [
    { value: "fuel_advance", label: "Fuel advance" },
    { value: "vehicle_finance", label: "Vehicle financing" },
    { value: "driver_advance", label: "Driver cash advance" },
];

const TENURES = [180, 365];
const PRODUCT_MIN = 100000;
const PRODUCT_MAX = 5000000;
const COLOR = "#7000FF";

export default function FleetFinanceApply() {
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [amount, setAmount] = useState(800000);
    const [tenure, setTenure] = useState(180);
    const [purpose, setPurpose] = useState("fuel_advance");
    const [busy, setBusy] = useState(false);
    const [quote, setQuote] = useState(null);
    const [openVeh, setOpenVeh] = useState(false);
    const [veh, setVeh] = useState({ plate: "", type: "truck", daily_km_avg: "", daily_earnings_avg: "", fuel_per_100km: "" });

    const refresh = () => api.get("/fleet/insights").then((r) => setData(r.data));
    useEffect(() => { refresh(); }, []);

    useEffect(() => {
        api.post("/loans/quote", { product: "FleetFinance", amount, tenure_days: tenure })
            .then((r) => setQuote(r.data)).catch(() => setQuote(null));
    }, [amount, tenure]);

    const addVehicle = async () => {
        if (!veh.plate) return toast.error("Plate required");
        await api.post("/fleet/vehicles", {
            plate: veh.plate,
            type: veh.type,
            daily_km_avg: parseFloat(veh.daily_km_avg || "0"),
            daily_earnings_avg: parseFloat(veh.daily_earnings_avg || "0"),
            fuel_per_100km: parseFloat(veh.fuel_per_100km || "0"),
        });
        setOpenVeh(false);
        setVeh({ plate: "", type: "truck", daily_km_avg: "", daily_earnings_avg: "", fuel_per_100km: "" });
        toast.success("Vehicle added");
        refresh();
    };

    const submit = async () => {
        setBusy(true);
        try {
            const r = await api.post("/fleet/apply", {
                amount,
                tenure_days: tenure,
                purpose,
            });
            toast.success("FleetFinance disbursed!");
            nav(`/business/loans/detail/${r.data.loan.id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    if (!data) return <div className="p-8 text-center text-slate-400">Loading fleet data…</div>;

    const ins = data.insights;
    const hasFleet = data.vehicle_count > 0;
    const riskColor = ins.risk_grade === "Low" ? "#00D084" : ins.risk_grade === "Medium" ? "#FF6B00" : "#FF0055";

    return (
        <div data-testid="fleet-apply-page">
            <MobileHeader title="FleetFinance™" subtitle="GPS-driven logistics credit" backTo="/business/loans" variant="blue">
                <div className="mt-4 bg-white/15 backdrop-blur rounded-2xl p-4 flex items-center gap-3">
                    <div className="size-12 rounded-xl bg-purple-400/30 flex items-center justify-center">
                        <Navigation className="text-purple-200" size={22} />
                    </div>
                    <div className="text-white flex-1">
                        <div className="text-xs uppercase tracking-wider text-white/70">Powered by</div>
                        <div className="font-display font-semibold">GPS · Telematics · Daily flows</div>
                    </div>
                </div>
            </MobileHeader>

            <div className="px-5 -mt-4 pb-4 space-y-4">
                {/* Fleet score */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow" data-testid="fleet-score-card">
                    <div className="flex items-center justify-between">
                        <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Fleet Activity Score</div>
                        <div className="text-xs px-2 py-1 rounded-md font-bold" style={{ background: `${riskColor}20`, color: riskColor }}>
                            {ins.risk_grade} risk
                        </div>
                    </div>
                    <div className="flex items-baseline gap-2 mt-3">
                        <span className="font-display text-5xl font-bold" style={{ color: riskColor }}>{ins.fleet_score}</span>
                        <span className="text-sm text-slate-500">/100</span>
                    </div>
                    <div className="mt-4 h-3 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${ins.fleet_score}%`, background: riskColor }} />
                    </div>
                </div>

                {/* Map mock with route lines */}
                <div className="rounded-2xl overflow-hidden relative h-44 border border-slate-200 bg-slate-100" data-testid="fleet-map-mock">
                    <svg viewBox="0 0 400 180" className="w-full h-full">
                        {/* Map grid background */}
                        <defs>
                            <pattern id="map-grid" width="20" height="20" patternUnits="userSpaceOnUse">
                                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#cbd5e1" strokeWidth="0.5" />
                            </pattern>
                            <linearGradient id="route-1" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%" stopColor="#7000FF" stopOpacity="0" />
                                <stop offset="50%" stopColor="#7000FF" stopOpacity="1" />
                                <stop offset="100%" stopColor="#0066FF" stopOpacity="1" />
                            </linearGradient>
                        </defs>
                        <rect width="400" height="180" fill="#f1f5f9" />
                        <rect width="400" height="180" fill="url(#map-grid)" />
                        {/* Routes */}
                        <path d="M 30 140 Q 100 80 200 100 T 370 50" stroke="url(#route-1)" strokeWidth="3" fill="none" strokeDasharray="0" />
                        <path d="M 60 30 Q 150 60 220 50 T 360 130" stroke="#0066FF" strokeWidth="2.5" fill="none" opacity="0.7" />
                        <path d="M 50 90 Q 130 110 210 130 T 380 90" stroke="#00D084" strokeWidth="2" fill="none" opacity="0.6" />
                        {/* Vehicle dots */}
                        <circle cx="200" cy="100" r="6" fill="#7000FF" stroke="white" strokeWidth="2" />
                        <circle cx="200" cy="100" r="12" fill="#7000FF" opacity="0.2">
                            <animate attributeName="r" values="6;14;6" dur="2s" repeatCount="indefinite" />
                            <animate attributeName="opacity" values="0.4;0;0.4" dur="2s" repeatCount="indefinite" />
                        </circle>
                        <circle cx="220" cy="50" r="5" fill="#0066FF" stroke="white" strokeWidth="2" />
                        <circle cx="210" cy="130" r="5" fill="#00D084" stroke="white" strokeWidth="2" />
                    </svg>
                    <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur rounded-lg px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Active vehicles</div>
                        <div className="font-display font-bold text-[#0A1930]">{data.vehicle_count}</div>
                    </div>
                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur rounded-lg px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Live</div>
                        <div className="text-xs font-bold flex items-center gap-1.5">
                            <span className="size-2 rounded-full bg-emerald-500 animate-pulse" /> Tracking
                        </div>
                    </div>
                </div>

                {/* KPIs */}
                <div className="grid grid-cols-3 gap-3">
                    <KPI icon={Gauge} color="#0066FF" label="Monthly km" value={ins.monthly_km.toLocaleString()} />
                    <KPI icon={Fuel} color="#FF6B00" label="Fuel eff." value={`${(ins.fuel_efficiency * 100).toFixed(0)}%`} />
                    <KPI icon={Clock} color="#00D084" label="On-time" value={`${ins.on_time_pct}%`} />
                </div>

                {/* Weekly activity */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow" data-testid="weekly-activity-chart">
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">7-day fleet activity</div>
                    <ResponsiveContainer width="100%" height={140}>
                        <BarChart data={ins.weekly_activity}>
                            <CartesianGrid stroke="#f1f5f9" vertical={false} />
                            <XAxis dataKey="day" axisLine={false} tickLine={false} fontSize={11} />
                            <YAxis hide />
                            <Tooltip
                                formatter={(v) => `${v} km`}
                                contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                            />
                            <Bar dataKey="km" fill={COLOR} radius={[6, 6, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                    <div className="text-xs text-slate-500 mt-2">
                        Monthly earnings: <span className="font-bold text-[#0A1930]">{fmtCurrency(ins.monthly_earnings, "NGN", { compact: true })}</span>
                    </div>
                </div>

                {/* Vehicles list */}
                <div className="bg-white rounded-2xl border border-slate-200 product-shadow overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                        <div className="font-display font-semibold text-[#0A1930]">Fleet vehicles</div>
                        <button onClick={() => setOpenVeh(true)} className="text-xs font-bold text-[#0066FF] flex items-center gap-1" data-testid="add-vehicle-btn">
                            <Plus size={14} /> Add
                        </button>
                    </div>
                    {data.vehicles.length === 0 && (
                        <div className="p-8 text-center text-sm text-slate-500">
                            Add at least one vehicle to qualify for FleetFinance
                        </div>
                    )}
                    {data.vehicles.map((v, i) => {
                        const VehIcon = VEHICLE_TYPES.find((t) => t.value === v.type)?.icon || Truck;
                        return (
                            <div key={v.id} className={`px-4 py-3 flex items-center gap-3 ${i !== data.vehicles.length - 1 ? "border-b border-slate-100" : ""}`} data-testid={`vehicle-${v.id}`}>
                                <div className="size-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
                                    <VehIcon size={18} />
                                </div>
                                <div className="flex-1">
                                    <div className="font-mono font-bold text-sm">{v.plate}</div>
                                    <div className="text-xs text-slate-500 capitalize">{v.type} · {v.daily_km_avg}km/day · {fmtCurrency(v.daily_earnings_avg, "NGN", { compact: true })}/day</div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Recommended limit */}
                {hasFleet && (
                    <div className="bg-purple-50 rounded-2xl border border-purple-200 p-4 flex items-center gap-3" data-testid="fleet-rec-limit">
                        <div className="size-11 rounded-xl bg-[#7000FF] text-white flex items-center justify-center">
                            <Navigation size={20} />
                        </div>
                        <div className="flex-1">
                            <div className="text-xs uppercase tracking-wider text-purple-700 font-bold">Recommended limit</div>
                            <div className="font-display text-xl font-bold text-purple-700">{fmtCurrency(data.recommended_limit_ngn, "NGN", { compact: true })}</div>
                        </div>
                        <div className="text-right">
                            <div className="text-xs text-purple-700">Approval</div>
                            <div className="font-display font-bold text-purple-700">{data.approval_likelihood}%</div>
                        </div>
                    </div>
                )}

                {/* Loan amount + tenure */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow">
                    <div className="text-sm font-semibold text-slate-700">How much do you need?</div>
                    <div className="text-center mt-4">
                        <div className="font-display text-3xl font-bold" style={{ color: COLOR }}>{fmtCurrency(amount)}</div>
                    </div>
                    <Slider min={PRODUCT_MIN} max={Math.min(PRODUCT_MAX, data.recommended_limit_ngn || PRODUCT_MAX)} step={10000} value={[amount]} onValueChange={(v) => setAmount(v[0])} className="mt-5" data-testid="fleet-amount-slider" />
                    <div className="flex justify-between text-xs text-slate-400 mt-3">
                        <span>{fmtCurrency(PRODUCT_MIN, "NGN", { compact: true })}</span>
                        <span>{fmtCurrency(Math.min(PRODUCT_MAX, data.recommended_limit_ngn || PRODUCT_MAX), "NGN", { compact: true })}</span>
                    </div>
                    <div className="mt-5 grid grid-cols-2 gap-2">
                        {TENURES.map((t) => (
                            <button key={t} onClick={() => setTenure(t)} data-testid={`fleet-tenure-${t}`} className={`py-3 rounded-xl border-2 text-sm font-semibold tap-feedback transition ${tenure === t ? "text-white" : "border-slate-200 text-slate-600"}`} style={tenure === t ? { background: COLOR, borderColor: COLOR } : {}}>
                                {t} days
                            </button>
                        ))}
                    </div>
                    <div className="mt-4">
                        <Label className="text-xs">Purpose</Label>
                        <Select value={purpose} onValueChange={setPurpose}>
                            <SelectTrigger className="h-10 mt-1 rounded-xl" data-testid="fleet-purpose"><SelectValue /></SelectTrigger>
                            <SelectContent>{PURPOSES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </div>

                {quote && (
                    <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow">
                        <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Repayment</div>
                        <div className="font-display text-2xl font-bold mt-1">{fmtCurrency(quote.total_repayment)}</div>
                        <div className="text-xs text-slate-500">Due in {tenure} days</div>
                    </div>
                )}

                <Button onClick={submit} disabled={busy || !hasFleet} className="w-full h-12 rounded-xl font-semibold" style={{ background: COLOR }} data-testid="fleet-submit-btn">
                    {busy ? "Approving…" : hasFleet ? "Apply with GPS Data" : "Add a vehicle to continue"}
                </Button>
            </div>

            <Dialog open={openVeh} onOpenChange={setOpenVeh}>
                <DialogContent className="rounded-2xl">
                    <DialogHeader><DialogTitle>Add vehicle</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Plate</Label><Input value={veh.plate} onChange={(e) => setVeh((s) => ({ ...s, plate: e.target.value.toUpperCase() }))} className="mt-1.5 h-11 rounded-xl font-mono" data-testid="veh-plate" /></div>
                        <div>
                            <Label>Type</Label>
                            <Select value={veh.type} onValueChange={(v) => setVeh((s) => ({ ...s, type: v }))}>
                                <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="veh-type"><SelectValue /></SelectTrigger>
                                <SelectContent>{VEHICLE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div><Label className="text-xs">Daily km</Label><Input type="number" value={veh.daily_km_avg} onChange={(e) => setVeh((s) => ({ ...s, daily_km_avg: e.target.value }))} className="mt-1 h-10 rounded-xl" data-testid="veh-km" /></div>
                            <div><Label className="text-xs">Daily earnings (₦)</Label><Input type="number" value={veh.daily_earnings_avg} onChange={(e) => setVeh((s) => ({ ...s, daily_earnings_avg: e.target.value }))} className="mt-1 h-10 rounded-xl" data-testid="veh-earnings" /></div>
                        </div>
                        <Button onClick={addVehicle} className="w-full h-11 rounded-xl bg-[#7000FF]" data-testid="veh-submit">Add Vehicle</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function KPI({ icon: Icon, color, label, value }) {
    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-3" data-testid={`fleet-kpi-${label.replace(/\s/g, "")}`}>
            <div className="size-9 rounded-lg flex items-center justify-center" style={{ background: `${color}15`, color }}>
                <Icon size={16} />
            </div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-2">{label}</div>
            <div className="font-display font-bold mt-0.5">{value}</div>
        </div>
    );
}
